import { supabase } from "@/lib";
import { chatCompletionService } from "@/services/openai/chatCompletionService";
import { useFileStorageStore, useThreadStore } from "@/store";
import { useCallback } from "react";
import { detectNeedsAnalysis, processFileForEmbeddingsSimple } from "../utils";

export const useItemMentionSelection = (
  textareaRef,
  message,
  setMessage,
  mentionStartRef,
  setMentionDropdownOpen,
  workspaceMemories,
  setFiles,
  setStorageFiles,
  setIsUploading,
  currentThreadId,
  toast
) => {
  const fileStorageStore = useFileStorageStore();

  // Function to fetch complete file information from the database
  const fetchCompleteFileInfo = useCallback(async (fileId) => {
    try {
      const { data, error } = await supabase
        .from("workspace_files")
        .select("*")
        .eq("id", fileId)
        .single();

      if (error) {
        console.error("Error fetching file info:", error);
        throw error;
      }

      if (!data) {
        throw new Error(`File with ID ${fileId} not found`);
      }

      return data;
    } catch (err) {
      console.error("Failed to fetch complete file info:", err);
      throw err;
    }
  }, []);

  // Handle item selection from mention dropdown
  const handleItemMentionSelect = useCallback(
    async (item) => {
      try {
        // SIMPLIFIED APPROACH: Always use last @ symbol position
        const textarea = textareaRef.current;
        const text = textarea?.value || message;
        const lastAtPos = text.lastIndexOf("@");

        // Always set the reference point to the last @ symbol position
        mentionStartRef.current = lastAtPos >= 0 ? lastAtPos : 0;

        // Clear any @ text by replacing it with empty string
        let newMessage = message;
        if (mentionStartRef.current !== null && mentionStartRef.current >= 0) {
          const atText = message.substring(mentionStartRef.current);
          const endOfMention = atText.search(/[\s$]/); // Find first space or end of string
          const endPos =
            endOfMention > 0
              ? mentionStartRef.current + endOfMention
              : message.length;

          newMessage =
            message.substring(0, mentionStartRef.current) +
            message.substring(endPos);
        }

        setMessage(newMessage);

        // If it's a memory, insert its name and content reference
        if (item.itemType === "memory") {
          try {
            // Find the memory in our loaded memories
            const memoryToUse = workspaceMemories.find((m) => m.id === item.id);

            if (memoryToUse) {
              // Create a blob to represent the memory content as a file
              const blob = new Blob([memoryToUse.content], {
                type: "text/plain",
              });

              // Create a file object with the memory content
              const file = new File([blob], `${memoryToUse.name}.txt`, {
                type: "text/plain",
              });

              // Add memory as a file reference directly
              const memoryFile = {
                id: `memory-${item.id}-${Date.now()}`,
                file, // Original file object
                name: `${memoryToUse.name}.txt`,
                type: "text/plain",
                size: blob.size,
                status: "uploaded",
                isMemory: true,
                memoryId: item.id,
                content: memoryToUse.content,
                // Add preview URL for this text file
                preview: URL.createObjectURL(blob),
              };

              // Add to files array
              setFiles((prev) => [...prev, memoryFile]);
            } else {
              console.error("Memory not found in loaded memories");
              toast({
                title: "Error",
                description: "Could not find the selected memory",
                variant: "destructive",
              });
            }
          } catch (error) {
            console.error("Error processing memory:", error);
            toast({
              title: "Error",
              description: "Failed to process the selected memory",
              variant: "destructive",
            });
          }
        }
        // If it's a file, handle it through the files state
        else if (item.itemType === "file") {
          setIsUploading(true);

          try {
            // Get complete file info from database
            const fileInfo = await fetchCompleteFileInfo(item.id);

            // Check if this file already has an OpenAI file ID
            if (fileInfo.openai_file_id) {
              // Process the file for preview capabilities
              try {
                // Get a download URL for the file to enable preview
                const { data: urlData } = await supabase.storage
                  .from("workspace")
                  .createSignedUrl(fileInfo.storage_path, 3600); // 1 hour expiry

                // For image files, ensure we're using Supabase storage in the chat bucket
                if (fileInfo.file_type?.startsWith("image/")) {
                  try {
                    // Download the image from workspace
                    const response = await fetch(urlData?.signedUrl);
                    const blob = await response.blob();
                    const imageFile = new File([blob], fileInfo.file_name, {
                      type: fileInfo.file_type,
                    });

                    // Upload to chat bucket
                    const chatUpload = await fileStorageStore.uploadChatFile(
                      imageFile,
                      currentThreadId
                    );

                    if (!chatUpload.error) {
                      // Create a processed file with all required properties
                      const processedFile = {
                        id: fileInfo.id,
                        openai_file_id: fileInfo.openai_file_id,
                        name: fileInfo.file_name,
                        type: fileInfo.file_type,
                        size: fileInfo.file_size,
                        status: "uploaded",
                        progress: 100,
                        thread_id: currentThreadId,
                        purpose: "assistants",
                        isImage: true,
                        path: chatUpload.data?.path,
                        publicUrl: chatUpload.data?.publicUrl,
                        url: chatUpload.data?.publicUrl,
                        preview: chatUpload.data?.publicUrl,
                        bucketName: "chat",
                        visionMetadata: {
                          publicUrl: chatUpload.data?.publicUrl,
                          contentType: fileInfo.file_type,
                          isReady: true,
                        },
                        metadata: fileInfo,
                      };

                      // Add to files array for use in the message
                      setFiles((prev) => [...prev, processedFile]);

                      // Add to storage files for thread association
                      setStorageFiles((prev) => [
                        ...prev,
                        {
                          ...processedFile,
                          data: {
                            path: chatUpload.data?.path,
                            publicUrl: chatUpload.data?.publicUrl,
                            name: fileInfo.file_name,
                          },
                        },
                      ]);

                      return;
                    }
                  } catch (chatUploadError) {
                    console.error(
                      "Error uploading image to chat bucket:",
                      chatUploadError
                    );
                    // Continue with normal flow if chat bucket upload fails
                  }
                }

                // Create a processed file with all required properties (non-image or fallback)
                const processedFile = {
                  id: fileInfo.id,
                  openai_file_id: fileInfo.openai_file_id,
                  name: fileInfo.file_name,
                  type: fileInfo.file_type,
                  size: fileInfo.file_size,
                  status: "uploaded",
                  progress: 100,
                  thread_id: currentThreadId,
                  purpose: "assistants",
                  isImage: fileInfo.file_type?.startsWith("image/") || false,
                  isDocument:
                    fileInfo.file_type?.includes("pdf") ||
                    fileInfo.file_type?.includes("document") ||
                    fileInfo.file_type?.includes("text/") ||
                    false,
                  requiresFunctionCall: true,
                  needsVision:
                    fileInfo.file_type?.startsWith("image/") || false,
                  needsFileSearch:
                    fileInfo.file_type?.includes("pdf") ||
                    fileInfo.file_type?.includes("document") ||
                    fileInfo.file_type?.includes("text/") ||
                    false,
                  path: fileInfo.storage_path,
                  publicUrl: fileInfo.public_url || urlData?.signedUrl,
                  url: urlData?.signedUrl, // Add signed URL for preview
                  preview: urlData?.signedUrl, // Add preview URL
                  bucketName: "workspace", // Use workspace bucket since these are workspace files
                  metadata: fileInfo, // Include all file metadata
                };

                // Add to files array for use in the message
                setFiles((prev) => [...prev, processedFile]);

                // Add to storage files for thread association
                setStorageFiles((prev) => [
                  ...prev,
                  {
                    ...processedFile,
                    data: {
                      path: fileInfo.storage_path,
                      publicUrl: fileInfo.public_url || urlData?.signedUrl,
                      name: fileInfo.file_name,
                    },
                  },
                ]);

                // Process for embeddings if file needs analysis
                if (processedFile.needsAnalysis) {
                  try {
                    const embeddingResult =
                      await processFileForEmbeddingsSimple({
                        ...processedFile,
                        thread_id: currentThreadId,
                        file_id: processedFile.openai_file_id,
                        name: processedFile.name,
                        size: processedFile.size,
                        type: processedFile.type,
                        path: processedFile.path,
                        storage_path: processedFile.path,
                        bucketName: processedFile.bucketName || "chat",
                        needsAnalysis: true,
                      });

                    if (embeddingResult) {
                      processedFile.embeddingJobId = embeddingResult.jobId;
                      processedFile.embeddingsProcessed = true;
                    } else {
                      console.warn(
                        "No embedding result returned for file:",
                        processedFile.name
                      );
                    }
                  } catch (embeddingError) {
                    console.error(
                      "Error processing embeddings:",
                      embeddingError
                    );
                    toast({
                      title: "Embedding Processing Error",
                      description: `Could not process file for embeddings: ${embeddingError.message}`,
                      variant: "warning",
                    });
                  }
                }

                // Update thread context with file reference
                try {
                  const { getThreadContext, setThreadContext } =
                    useThreadStore.getState();
                  const currentContext = getThreadContext(currentThreadId);

                  if (currentContext) {
                    const fileRef = {
                      id: fileInfo.id,
                      path: fileInfo.storage_path,
                      name: fileInfo.file_name,
                      type: fileInfo.file_type,
                      size: fileInfo.file_size,
                      thread_id: currentThreadId,
                      url: urlData?.signedUrl,
                      openai_file_id: fileInfo.openai_file_id,
                      purpose: "assistants",
                      isImage:
                        fileInfo.file_type?.startsWith("image/") || false,
                      isDocument:
                        fileInfo.file_type?.includes("pdf") ||
                        fileInfo.file_type?.includes("document") ||
                        fileInfo.file_type?.includes("text/") ||
                        false,
                      created_at: new Date().toISOString(),
                      requiresFunctionCall: true,
                      needsVision:
                        fileInfo.file_type?.startsWith("image/") || false,
                      needsFileSearch:
                        fileInfo.file_type?.includes("pdf") ||
                        fileInfo.file_type?.includes("document") ||
                        fileInfo.file_type?.includes("text/") ||
                        false,
                      needsAnalysis: detectNeedsAnalysis({
                        type: fileInfo.file_type,
                        name: fileInfo.file_name,
                      }),
                      embeddingJobId: processedFile.embeddingJobId,
                      embeddingsProcessed:
                        processedFile.embeddingsProcessed || false,
                    };

                    setThreadContext({
                      ...currentContext,
                      storage_files: [
                        ...(currentContext.storage_files || []),
                        fileRef,
                      ],
                    });
                  }
                } catch (err) {
                  console.warn(
                    "Failed to update thread context with referenced file:",
                    err
                  );
                }
              } catch (previewErr) {
                console.warn("Error generating preview URL:", previewErr);
                // Continue with file without preview
              }
            } else {
              // Get the file storage path
              const storagePath = fileInfo.storage_path;
              if (!storagePath) {
                throw new Error("File storage path not found");
              }

              // Get a download URL for the file
              const { data: urlData } = await supabase.storage
                .from("workspace")
                .createSignedUrl(storagePath, 3600); // 1 hour expiry

              if (!urlData?.signedUrl) {
                throw new Error("Could not generate download URL");
              }

              // Download the file
              const response = await fetch(urlData.signedUrl);
              const blob = await response.blob();

              // Create a File object
              const file = new File([blob], fileInfo.file_name, {
                type: fileInfo.file_type,
              });

              // Upload to OpenAI
              const uploaded = await chatCompletionService.uploadFile(
                file,
                "assistants"
              );

              if (!uploaded?.id) {
                throw new Error("OpenAI file upload failed");
              }

              // Update the file in the database with the OpenAI file ID
              await supabase
                .from("workspace_files")
                .update({ openai_file_id: uploaded.id })
                .eq("id", fileInfo.id);

              // Create a processed file object
              const processedFile = {
                id: fileInfo.id,
                openai_file_id: uploaded.id,
                name: fileInfo.file_name,
                type: fileInfo.file_type,
                size: fileInfo.file_size,
                status: "uploaded",
                progress: 100,
                thread_id: currentThreadId,
                purpose: "assistants",
                isImage: fileInfo.file_type?.startsWith("image/") || false,
                isDocument:
                  fileInfo.file_type?.includes("pdf") ||
                  fileInfo.file_type?.includes("document") ||
                  fileInfo.file_type?.includes("text/") ||
                  false,
                requiresFunctionCall: true,
                needsVision: fileInfo.file_type?.startsWith("image/") || false,
                needsFileSearch:
                  fileInfo.file_type?.includes("pdf") ||
                  fileInfo.file_type?.includes("document") ||
                  fileInfo.file_type?.includes("text/") ||
                  false,
                needsAnalysis: detectNeedsAnalysis({
                  type: fileInfo.file_type,
                  name: fileInfo.file_name,
                }),
                path: fileInfo.storage_path,
                publicUrl: fileInfo.public_url || urlData?.signedUrl,
                url: urlData?.signedUrl, // Add signed URL
                preview: URL.createObjectURL(blob), // Create preview URL from blob
                file: file, // Store the actual file object for preview
                bucketName: "workspace",
                metadata: fileInfo, // Include all file metadata
              };

              // Add to files array for use in the message
              setFiles((prev) => [...prev, processedFile]);

              // Add to storage files for thread association
              setStorageFiles((prev) => [
                ...prev,
                {
                  ...processedFile,
                  data: {
                    path: fileInfo.storage_path,
                    publicUrl: fileInfo.public_url || urlData?.signedUrl,
                    name: fileInfo.file_name,
                  },
                },
              ]);

              // Process for embeddings if file needs analysis
              if (processedFile.needsAnalysis) {
                try {
                  const embeddingResult = await processFileForEmbeddingsSimple({
                    ...processedFile,
                    thread_id: currentThreadId,
                    file_id: processedFile.openai_file_id,
                  });

                  if (embeddingResult) {
                    processedFile.embeddingJobId = embeddingResult.jobId;
                    processedFile.embeddingsProcessed = true;
                  } else {
                    console.warn(
                      "No embedding result returned for file:",
                      processedFile.name
                    );
                  }
                } catch (embeddingError) {
                  console.error("Error processing embeddings:", embeddingError);
                  toast({
                    title: "Embedding Processing Error",
                    description: `Could not process file for embeddings: ${embeddingError.message}`,
                    variant: "warning",
                  });
                }
              }

              // Update thread context with file reference
              try {
                const { getThreadContext, setThreadContext } =
                  useThreadStore.getState();
                const currentContext = getThreadContext(currentThreadId);

                if (currentContext) {
                  const fileRef = {
                    id: fileInfo.id,
                    path: fileInfo.storage_path,
                    name: fileInfo.file_name,
                    type: fileInfo.file_type,
                    size: fileInfo.file_size,
                    thread_id: currentThreadId,
                    url: urlData?.signedUrl,
                    openai_file_id: uploaded.id,
                    purpose: "assistants",
                    isImage: fileInfo.file_type?.startsWith("image/") || false,
                    isDocument:
                      fileInfo.file_type?.includes("pdf") ||
                      fileInfo.file_type?.includes("document") ||
                      fileInfo.file_type?.includes("text/") ||
                      false,
                    created_at: new Date().toISOString(),
                    requiresFunctionCall: true,
                    needsVision:
                      fileInfo.file_type?.startsWith("image/") || false,
                    needsFileSearch:
                      fileInfo.file_type?.includes("pdf") ||
                      fileInfo.file_type?.includes("document") ||
                      fileInfo.file_type?.includes("text/") ||
                      false,
                    needsAnalysis: detectNeedsAnalysis({
                      type: fileInfo.file_type,
                      name: fileInfo.file_name,
                    }),
                    embeddingJobId: processedFile.embeddingJobId,
                    embeddingsProcessed:
                      processedFile.embeddingsProcessed || false,
                  };

                  setThreadContext({
                    ...currentContext,
                    storage_files: [
                      ...(currentContext.storage_files || []),
                      fileRef,
                    ],
                  });
                }
              } catch (err) {
                console.warn(
                  "Failed to update thread context with referenced file:",
                  err
                );
              }
            }
          } catch (error) {
            console.error("Error referencing file:", error);
            toast({
              title: "Error",
              description: `Could not reference file: ${error.message}`,
              variant: "destructive",
            });
          } finally {
            setIsUploading(false);
          }
        } else {
          console.error("Unknown item type:", item);
        }

        setMentionDropdownOpen(false);
        mentionStartRef.current = null;

        // Force focus back to textarea after selection
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
        }, 50);
      } catch (error) {
        console.error("Error handling item mention:", error);
        toast({
          title: "Error",
          description: "Failed to handle item mention",
          variant: "destructive",
        });
        setIsUploading(false);
      }
    },
    [
      textareaRef,
      message,
      setMessage,
      mentionStartRef,
      setMentionDropdownOpen,
      workspaceMemories,
      setFiles,
      setStorageFiles,
      setIsUploading,
      currentThreadId,
      toast,
      fileStorageStore,
      fetchCompleteFileInfo,
    ]
  );

  return {
    handleItemMentionSelect,
    fetchCompleteFileInfo,
  };
};
